import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { lazyWithRetry, lazyEmailRoute } from './lazyWithRetry';
import EmailsDetail from './modules/emails/Detail';
import AppointmentsForm from './modules/crm-appointments/Form';
import AppointmentsDetail from './modules/crm-appointments/Detail';
import TodosForm from './modules/crm-todos/Form';
import TodosDetail from './modules/crm-todos/Detail';
import TodosCalendarView from './modules/crm-todos/TodosCalendarView';
import AppointmentsCalendarView from './modules/crm-appointments/CalendarView';
import DashboardList from './modules/dashboard/List';
import AiChatPage from './modules/ai-chat/AiChatPage';
import AiPromptsPage from './modules/ai-prompts/AiPromptsPage';
import AiUsagePage from './modules/ai-usage/AiUsagePage';
import EmailLibraryList from './modules/email-library/List';
import SystemHealthPage from './modules/system-health/SystemHealthPage';
import SystemHealthStatusPage from './modules/system-health/SystemHealthStatusPage';
import { DatabaseBrowserPage } from './modules/database-browser';
import ImportOrganizationsPage from './modules/tools/ImportOrganizationsPage';
import ActivityLogsList from './modules/activity-logs/List';
import ActivityLogsDetail from './modules/activity-logs/Detail';
import CustomFieldsList from './modules/custom-fields/List';
import CustomFieldsForm from './modules/custom-fields/Form';
import CustomFieldsDetail from './modules/custom-fields/Detail';
import FieldConfigurator from './modules/custom-fields/FieldConfigurator';
import SearchFoldersList from './modules/search-folders/List';
import SearchFoldersDetail from './modules/search-folders/Detail';
import SearchFoldersForm from './modules/search-folders/Form';
import EmailLogsPage from './modules/email-logs/EmailLogsPage';
import UsersList from './modules/users/List';
import UsersForm from './modules/users/Form';
import UsersDetail from './modules/users/Detail';
import UserPermissionsPage from './modules/users/UserPermissionsPage';
import ClientsList from './modules/clients/List';
import ClientsForm from './modules/clients/Form';
import ClientsDetail from './modules/clients/Detail';
import ClientSummaryPage from './modules/clients/ClientSummaryPage';
import EmailAccountsList from './modules/email-accounts/List';
import EmailAccountsForm from './modules/email-accounts/Form';
import EmailAccountsDetail from './modules/email-accounts/Detail';
import EmailSignaturesList from './modules/email-signatures/List';
import EmailSignaturesView from './modules/email-signatures/View';
import EmailSignaturesForm from './modules/email-signatures/Form';
import EmailRulesList from './modules/email-rules/List';
import EmailRulesForm from './modules/email-rules/Form';
import EmailRulesDetail from './modules/email-rules/Detail';
import { ConfigurationSettingsPage, ParametersPage } from './modules/configuration-settings';

export const RouteLoadFallback: React.FC = () => {
  const { t } = useTranslation('common');
  return (
    <div className="flex items-center justify-center min-h-[280px]">
      <div className="text-gray-500 dark:text-gray-400">{t('states.loadingPage')}</div>
    </div>
  );
};

export const LazySuspense: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<RouteLoadFallback />}>{children}</Suspense>
);

/** Settings hub (already split) */
export const SettingsPageLazy = lazyWithRetry(() => import('./modules/settings').then((m) => ({ default: m.default })));
export const FilesStoragePageLazy = lazyWithRetry(() =>
  import('./modules/settings').then((m) => ({ default: m.FilesStoragePage }))
);
export const SystemEmailTemplatesPageLazy = lazyWithRetry(() =>
  import('./modules/settings').then((m) => ({ default: m.SystemEmailTemplatesPage }))
);
export const EmailNotificationSettingsPageLazy = lazyWithRetry(() =>
  import('./modules/settings').then((m) => ({ default: m.EmailNotificationSettingsPage }))
);
export const GoogleSyncSettingsPageLazy = lazyWithRetry(() =>
  import('./modules/settings').then((m) => ({ default: m.GoogleSyncSettingsPage }))
);

/** Tools — static import for reliable LAN navigation */
export const DatabaseBrowserPageLazy = DatabaseBrowserPage;
export const ImportOrganizationsPageLazy = ImportOrganizationsPage;

/** Dashboard */
export const DashboardListLazy = DashboardList;

/** AI Chat */
export const AiChatPageLazy = AiChatPage;
export const AiPromptsPageLazy = AiPromptsPage;
export const AiUsagePageLazy = AiUsagePage;

/** Clients */
export const ClientsListLazy = ClientsList;
export const ClientsFormLazy = ClientsForm;
export const ClientsDetailLazy = ClientsDetail;
export const ClientSummaryPageLazy = ClientSummaryPage;

/** CRM todos / appointments */
export const TodosFormLazy = TodosForm;
export const TodosDetailLazy = TodosDetail;
export const TodosCalendarViewLazy = TodosCalendarView;

/** CRM appointments */
export const AppointmentsFormLazy = AppointmentsForm;
export const AppointmentsDetailLazy = AppointmentsDetail;
export const AppointmentsCalendarViewLazy = AppointmentsCalendarView;

/** Activity logs */
export const ActivityLogsListLazy = ActivityLogsList;
export const ActivityLogsDetailLazy = ActivityLogsDetail;

/** Custom fields */
export const CustomFieldsListLazy = CustomFieldsList;
export const CustomFieldsFormLazy = CustomFieldsForm;
export const CustomFieldsDetailLazy = CustomFieldsDetail;
export const FieldConfiguratorLazy = FieldConfigurator;

/** Configuration */
export const ConfigurationSettingsPageLazy = ConfigurationSettingsPage;
export const ParametersPageLazy = ParametersPage;

/** Email accounts & related */
export const EmailAccountsListLazy = EmailAccountsList;
export const EmailAccountsFormLazy = EmailAccountsForm;
export const EmailAccountsDetailLazy = EmailAccountsDetail;
export const EmailSignaturesListLazy = EmailSignaturesList;
export const EmailSignaturesViewLazy = EmailSignaturesView;
export const EmailSignaturesFormLazy = EmailSignaturesForm;
export const SearchFoldersListLazy = SearchFoldersList;
export const SearchFoldersDetailLazy = SearchFoldersDetail;
export const SearchFoldersFormLazy = SearchFoldersForm;
export const EmailRulesListLazy = EmailRulesList;
export const EmailRulesFormLazy = EmailRulesForm;
export const EmailRulesDetailLazy = EmailRulesDetail;
export const EmailLibraryListLazy = EmailLibraryList;

/** Email detail — static import avoids flaky async chunk timeouts over LAN in dev */
export const EmailsDetailLazy = EmailsDetail;
export const EmailsListLazy = lazyEmailRoute(() =>
  import(/* webpackMode: "eager" */ './modules/emails/List'),
);
export const EmailsComposeLazy = lazyEmailRoute(() =>
  import(/* webpackMode: "eager" */ './modules/emails/Compose'),
);

/** Parameters */
export const TaxAuthoritiesListLazy = lazyWithRetry(() => import('./modules/parameters/tax_authority/List'));
export const TaxAuthoritiesFormLazy = lazyWithRetry(() => import('./modules/parameters/tax_authority/Form'));
export const TaxAuthoritiesDetailLazy = lazyWithRetry(() => import('./modules/parameters/tax_authority/Detail'));
export const CountriesListLazy = lazyWithRetry(() => import('./modules/parameters/p_country/List'));
export const CountriesFormLazy = lazyWithRetry(() => import('./modules/parameters/p_country/Form'));
export const CountriesDetailLazy = lazyWithRetry(() => import('./modules/parameters/p_country/Detail'));
export const AppStatusListLazy = lazyWithRetry(() => import('./modules/parameters/app_status/List'));
export const AppStatusFormLazy = lazyWithRetry(() => import('./modules/parameters/app_status/Form'));
export const AppStatusDetailLazy = lazyWithRetry(() => import('./modules/parameters/app_status/Detail'));
export const AppDurationListLazy = lazyWithRetry(() => import('./modules/parameters/duration/List'));
export const AppDurationFormLazy = lazyWithRetry(() => import('./modules/parameters/duration/Form'));
export const AppDurationDetailLazy = lazyWithRetry(() => import('./modules/parameters/duration/Detail'));
export const TagsListLazy = lazyWithRetry(() => import('./modules/parameters/tags/List'));
export const TagsFormLazy = lazyWithRetry(() => import('./modules/parameters/tags/Form'));
export const TagsDetailLazy = lazyWithRetry(() => import('./modules/parameters/tags/Detail'));

/** Users admin */
export const UsersListLazy = UsersList;
export const UsersFormLazy = UsersForm;
export const UsersDetailLazy = UsersDetail;
export const UserPermissionsPageLazy = UserPermissionsPage;

/** System & profile */
export const EmailLogsPageLazy = EmailLogsPage;
export const SystemHealthPageLazy = SystemHealthPage;
export const SystemHealthStatusPageLazy = SystemHealthStatusPage;
export const ProfileSettingsLazy = lazyWithRetry(() => import('./components/ProfileSettings'));
export const PreferencesLazy = lazyWithRetry(() => import('./components/Preferences'));

export const FileSendUploadPageLazy = lazyWithRetry(() =>
  import('./modules/file-send').then((m) => ({ default: m.FileSendUploadPage })),
);
export const FileSendDownloadPageLazy = lazyWithRetry(() =>
  import('./modules/file-send').then((m) => ({ default: m.FileSendDownloadPage })),
);
